import * as React from 'react'
import { isFragment } from 'react-is'
import clsx from 'clsx'
import { SxProps } from '../sx'
import { SlotComponentProps } from '../utils'
import { useSlotProps } from '../utils/useSlotProps'
import { SvgIcon } from '../SvgIcon'
import { BreadcrumbCollapsed } from './BreadcrumbCollapsed'
import { ButtonBaseProps } from '../ButtonBase'
import { Typography, TypographyProps } from '../Typography'
import styles from './index.module.scss'

export interface BreadcrumbsClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the ol element. */
  ol: string
  /** Styles applied to the li element. */
  li: string
  /** Styles applied to the separator element. */
  separator: string
}

export type HTMLNavElement = React.ElementType<'nav'>

/* eslint-disable-next-line @typescript-eslint/no-empty-interface */
export interface BreadcrumbsCollapsedIconSlotPropsOverrides {}

export interface BreadcrumbsOwnerState extends BreadcrumbsProps {
  expanded: boolean
}

export interface BreadcrumbsProps extends React.HTMLAttributes<HTMLNavElement> {
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component?: TypographyProps['component']
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<BreadcrumbsClasses>
  /**
   * The components used for each slot inside the Breadcumb.
   * Either a string to use a HTML element or a component.
   * @default {}
   */
  slots?: {
    CollapsedIcon?: React.ElementType
  }
  /**
   * The props used for each slot inside the Breadcumb.
   * @default {}
   */
  slotProps?: {
    /**
     * Props applied to the CollapsedButton.
     * @default {}
     */
    collapsedButton?: ButtonBaseProps
    /**
     * Props applied to the CollapsedIcon slot.
     * @default {}
     */
    collapsedIcon?: SlotComponentProps<
      typeof SvgIcon,
      BreadcrumbsCollapsedIconSlotPropsOverrides,
      BreadcrumbsOwnerState
    >
  }
  /**
   * Override the default label for the expand button.
   *
   * For localization purposes, you can use the provided [translations](/material-ui/guides/localization/).
   * @default 'Show path'
   */
  expandText?: string
  /**
   * If max items is exceeded, the number of items to show after the ellipsis.
   * @default 1
   */
  itemsAfterCollapse?: number
  /**
   * If max items is exceeded, the number of items to show before the ellipsis.
   * @default 1
   */
  itemsBeforeCollapse?: number
  /**
   * Specifies the maximum number of breadcrumbs to display. When there are more
   * than the maximum number, only the first `itemsBeforeCollapse` and last `itemsAfterCollapse`
   * will be shown, with an ellipsis in between.
   * @default 8
   */
  maxItems?: number
  /**
   * Custom separator node.
   * @default '/'
   */
  separator?: React.ReactNode
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const BreadcrumbsRoot = React.forwardRef<
  HTMLNavElement,
  TypographyProps
>((props, ref) => {
  const { children, ...others } = props
  return (
    // @ts-ignore
    <Typography ref={ref} {...others}>
      {children}
    </Typography>
  )
})
BreadcrumbsRoot.displayName = 'BreadcrumbsRoot'

export const BreadcrumbsOl = React.forwardRef<
  HTMLOListElement,
  React.HTMLAttributes<HTMLOListElement>
>((props, ref) => {
  const { children, className, ...others } = props
  return (
    <ol ref={ref} {...others} className={clsx(styles.ol, className)}>
      {children}
    </ol>
  )
})
BreadcrumbsOl.displayName = 'BreadcrumbsOl'

export const BreadcrumbsSeparator = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>((props, ref) => {
  const { children, className, ...others } = props
  return (
    <li ref={ref} {...others} className={clsx(styles.li, className)}>
      {children}
    </li>
  )
})

BreadcrumbsSeparator.displayName = 'BreadcrumbsSeparator'

function insertSeparators(
  items: JSX.Element[],
  className: string | undefined,
  separator: React.ReactNode
) {
  return items.reduce<JSX.Element[]>((acc, current, index) => {
    if (index < items.length - 1) {
      acc = acc.concat(
        current,
        <BreadcrumbsSeparator
          aria-hidden
          key={`separator-${index}`}
          className={className}
        >
          {separator}
        </BreadcrumbsSeparator>
      )
    } else {
      acc.push(current)
    }

    return acc
  }, [])
}

export const Breadcrumbs = React.forwardRef<HTMLNavElement, BreadcrumbsProps>(
  function Breadcrumbs(props, ref) {
    const {
      children,
      className,
      classes,
      component = 'nav',
      slots = {},
      slotProps = {},
      expandText = 'Show path',
      itemsAfterCollapse = 1,
      itemsBeforeCollapse = 1,
      maxItems = 8,
      separator = '/',
      ...other
    } = props

    const [expanded, setExpanded] = React.useState(false)

    const ownerState = {
      ...props,
      component,
      expanded,
      expandText,
      itemsAfterCollapse,
      itemsBeforeCollapse,
      maxItems,
      separator,
    }

    const collapsedIconSlotProps = useSlotProps({
      elementType: slots.CollapsedIcon,
      externalSlotProps: slotProps.collapsedIcon,
      ownerState,
    })

    const listRef = React.useRef<HTMLOListElement | null>(null)
    const renderItemsBeforeAndAfter = (allItems: JSX.Element[]) => {
      const handleClickExpand = () => {
        setExpanded(true)

        // The clicked element received the focus but gets removed from the DOM.
        // Let's keep the focus in the component after expanding.
        // Moving it to the <ol> or <nav> does not cause any announcement in NVDA.
        // By moving it to some link/button at least we have some announcement.
        const focusable = listRef.current?.querySelector(
          'a[href],button,[tabindex]'
        )
        if (focusable) {
          // @ts-ignore
          focusable.focus()
        }
      }

      // This defends against someone passing weird input, to ensure that if all
      // items would be shown anyway, we just show all items without the EllipsisItem
      if (itemsBeforeCollapse + itemsAfterCollapse >= allItems.length) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(
            [
              'You have provided an invalid combination of props to the Breadcrumbs.',
              `itemsAfterCollapse={${itemsAfterCollapse}} + itemsBeforeCollapse={${itemsBeforeCollapse}} >= maxItems={${maxItems}}`,
            ].join('\n')
          )
        }
        return allItems
      }

      return [
        ...allItems.slice(0, itemsBeforeCollapse),
        <BreadcrumbCollapsed
          {...slotProps?.collapsedButton}
          aria-label={expandText}
          key="ellipsis"
          slots={{ CollapsedIcon: slots.CollapsedIcon }}
          // @ts-ignore
          slotProps={{ collapsedIcon: collapsedIconSlotProps }}
          onClick={handleClickExpand}
        />,
        ...allItems.slice(
          allItems.length - itemsAfterCollapse,
          allItems.length
        ),
      ]
    }

    const allItems = React.Children.toArray(children)
      .filter(child => {
        if (process.env.NODE_ENV !== 'production') {
          if (isFragment(child)) {
            console.error(
              [
                "The Breadcrumbs component doesn't accept a Fragment as a child.",
                'Consider providing an array instead.',
              ].join('\n')
            )
          }
        }

        return React.isValidElement(child)
      })
      .map((child, index) => (
        <li className={classes?.li} key={`child-${index}`}>
          {child}
        </li>
      ))

    return (
      // @ts-ignore
      <BreadcrumbsRoot
        ref={ref}
        component={component}
        className={clsx(classes?.root, className)}
        {...other}
      >
        <BreadcrumbsOl className={classes?.ol} ref={listRef}>
          {insertSeparators(
            expanded || (maxItems && allItems.length <= maxItems)
              ? allItems
              : renderItemsBeforeAndAfter(allItems),
            classes?.separator,
            separator
          )}
        </BreadcrumbsOl>
      </BreadcrumbsRoot>
    )
  }
)
